// "Needs attention" monitoring rules — pure, derived entirely from data already
// loaded on the client screen. Turns a dense dashboard into a decision-oriented
// one: drift, excess cash, concentration, overdue review, stale valuations.
import { daysOld, daysOldDateOnly, isDateOnly } from './format.js';
import { groupHoldings } from './exposures.js';

export type AlertSeverity = 'high' | 'medium' | 'info';

export interface PortfolioAlert {
  id: string;
  severity: AlertSeverity;
  title: string;
  detail: string;
}

export interface AlertClientInput {
  reviewDue?: string | null;
  /** Target weights by raw asset-class code (equity_au, …), as decimals 0..1. */
  targets?: Record<string, number | null | undefined>;
}

export interface AlertHolding {
  code: string;
  name?: string | null;
  currentValue: number; // dollars
}

export interface AlertPortfolioInput {
  tnw: number;   // dollars (managed portfolio value)
  cash: number;  // dollars
  allocation: Array<{ bucket: string; value: number; pct: number }>; // bucket = raw code, pct 0..1
  holdings: AlertHolding[]; // flat across accounts
}

const CASH_CODE = 'cash';

export function buildPortfolioAlerts(args: {
  client: AlertClientInput;
  portfolio: AlertPortfolioInput;
  asOf?: string | null;
  driftThreshold?: number; // default 0.05 (5%)
  staleDays?: number;      // default 3
  today?: string;          // YYYY-MM-DD (defaults to AU today) — injectable for tests
}): PortfolioAlert[] {
  const { client, portfolio } = args;
  const threshold = args.driftThreshold ?? 0.05;
  const staleDays = args.staleDays ?? 3;
  const alerts: PortfolioAlert[] = [];
  const pctOf = (v: number) => (v * 100).toFixed(1);
  const actualByBucket = new Map(portfolio.allocation.map((s) => [s.bucket, s.pct]));

  // 1) Overdue review (high) — calendar-date comparison; due *today* is not overdue.
  if (client.reviewDue && isDateOnly(client.reviewDue)) {
    const d = args.today ? daysOldDateOnly(client.reviewDue, args.today) : daysOldDateOnly(client.reviewDue);
    if (d != null && d > 0) {
      alerts.push({ id: 'review-overdue', severity: 'high', title: 'Review overdue',
        detail: `Annual review was due ${d} day${d === 1 ? '' : 's'} ago.` });
    }
  }

  // 2) Stale valuations (high) — only when asOf is supplied (callers without a
  //    valuation date pass nothing and skip this rule rather than false-flag).
  if (args.asOf !== undefined && portfolio.tnw > 0) {
    const d = daysOld(args.asOf);
    if (d == null) {
      alerts.push({ id: 'valuation-missing', severity: 'high', title: 'No recent valuation',
        detail: 'Portfolio has no dated valuation — figures may be unreliable.' });
    } else if (d > staleDays) {
      alerts.push({ id: 'valuation-stale', severity: 'high', title: 'Valuations are stale',
        detail: `Latest valuation is ${d} days old. Refresh prices before relying on the figures.` });
    }
  }

  // 3) Allocation drift (medium) — only where a target is set.
  if (client.targets) {
    for (const [code, rawTarget] of Object.entries(client.targets)) {
      if (code === CASH_CODE) continue; // cash drift is handled separately below
      const target = typeof rawTarget === 'number' ? rawTarget : null;
      if (target == null) continue;
      const actual = actualByBucket.get(code) ?? 0;
      const drift = actual - target;
      if (Math.abs(drift) >= threshold) {
        alerts.push({
          id: `drift-${code}`, severity: 'medium',
          title: `${labelFor(code)} ${drift > 0 ? 'overweight' : 'underweight'}`,
          detail: `${pctOf(actual)}% actual vs ${pctOf(target)}% target (${drift > 0 ? '+' : ''}${pctOf(drift)}%).`,
        });
      }
    }
  }

  // 4) Excess cash (medium/info)
  if (portfolio.tnw > 0) {
    const cashPct = portfolio.cash / portfolio.tnw;
    const cashTarget = client.targets?.[CASH_CODE];
    if (typeof cashTarget === 'number' && cashPct - cashTarget >= threshold) {
      alerts.push({ id: 'cash-excess', severity: 'medium', title: 'Excess cash',
        detail: `Cash is ${pctOf(cashPct)}% vs ${pctOf(cashTarget)}% target — ${(portfolio.cash >= 0 ? '' : '')}consider deploying.` });
    } else if (cashTarget == null && cashPct >= 0.10) {
      alerts.push({ id: 'cash-high', severity: 'info', title: 'High cash weighting',
        detail: `Cash is ${pctOf(cashPct)}% of the portfolio with no target set.` });
    }
  }

  // 5) Concentration (medium single / info top-3)
  if (portfolio.tnw > 0 && portfolio.holdings.length > 0) {
    const grouped = groupHoldings(
      portfolio.holdings.map((h) => ({ code: h.code, name: h.name, currentValue: h.currentValue })),
      portfolio.tnw,
    ).sort((a, b) => b.value - a.value);
    const top = grouped[0];
    if (top && top.exposurePct >= 0.10) {
      alerts.push({ id: `concentration-${top.code}`, severity: 'medium', title: 'Single-holding concentration',
        detail: `${top.code}${top.name ? ` (${top.name})` : ''} is ${pctOf(top.exposurePct)}% of the portfolio.` });
    }
    const top3 = grouped.slice(0, 3).reduce((s, r) => s + r.exposurePct, 0);
    if (grouped.length >= 3 && top3 >= 0.40) {
      alerts.push({ id: 'concentration-top3', severity: 'info', title: 'Concentrated in few holdings',
        detail: `Top 3 holdings are ${pctOf(top3)}% of the portfolio.` });
    }
  }

  return alerts;
}

const LABELS: Record<string, string> = {
  equity_au: 'Australian shares', equity_intl: 'International shares', property: 'Property',
  fi: 'Fixed income', cash: 'Cash', alt: 'Alternatives',
};
function labelFor(code: string): string { return LABELS[code] ?? code; }
